/**
 * MM2 (Komodo DeFi Framework) Service
 * Connects to the mm2 daemon for atomic swaps via the Komodo DEX network
 */

interface MM2Config {
  apiUrl: string;
  rpcPassword: string;
}

interface Coin {
  coin: string;
  name: string;
  fname: string;
  rpcport: number;
  pubtype: number;
  p2shtype: number;
  wiftype: number;
  txfee: number;
  mm2: number;
  required_confirmations: number;
  avg_blocktime: number;
  protocol: { type: string };
}

interface OrderbookEntry {
  coin: string;
  address: string;
  price: string;
  price_rat: [string, string];
  price_fraction: { numer: string; denom: string };
  maxvolume: string;
  max_volume_rat: [string, string];
  max_volume_fraction: { numer: string; denom: string };
  min_volume: string;
  pubkey: string;
  age: number;
  zcredits: number;
  uuid: string;
  is_mine: boolean;
}

interface Orderbook {
  askdepth: number;
  asks: OrderbookEntry[];
  biddepth: number;
  bids: OrderbookEntry[];
  netid: number;
  numasks: number;
  numbids: number;
  rel: string;
  base: string;
  timestamp: number;
  total_asks_base_vol: string;
  total_asks_rel_vol: string;
  total_bids_base_vol: string;
  total_bids_rel_vol: string;
}

interface SwapStatus {
  uuid: string;
  my_order_uuid?: string;
  events: SwapEvent[];
  success_events: string[];
  error_events: string[];
  type: 'Maker' | 'Taker';
  my_info?: {
    my_coin: string;
    other_coin: string;
    my_amount: string;
    other_amount: string;
    started_at: number;
  };
}

interface SwapEvent {
  event: {
    type: string;
    data?: any;
  };
  timestamp: number;
}

interface EnableCoinResponse {
  result: string;
  address: string;
  balance: string;
  unspendable_balance: string;
  coin: string;
  required_confirmations: number;
  mature_confirmations?: number;
}

class MM2Service {
  private config: MM2Config;
  private enabledCoins: Set<string> = new Set();

  constructor() {
    this.config = {
      // Use direct IP for now, will switch to dex-api.wattxchange.app once DNS is set
      apiUrl: 'https://dex-api.wattxchange.app',
      rpcPassword: 'wattxchange_mm2_rpc'
    };
  }

  /**
   * Make an RPC call to mm2
   */
  private async rpc(method: string, params: Record<string, any> = {}): Promise<any> {
    try {
      const response = await fetch(this.config.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userpass: this.config.rpcPassword,
          method,
          ...params
        })
      });

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      return data;
    } catch (error) {
      console.error(`MM2 RPC error (${method}):`, error);
      throw error;
    }
  }

  /**
   * Get mm2 version
   */
  async getVersion(): Promise<string> {
    const response = await this.rpc('version');
    return response.result;
  }

  /**
   * Enable a UTXO coin for trading
   */
  async enableUtxoCoin(
    coin: string,
    electrumServers: { url: string; protocol?: string }[]
  ): Promise<EnableCoinResponse> {
    const response = await this.rpc('electrum', {
      coin,
      servers: electrumServers,
      mm2: 1,
      tx_history: true
    });

    this.enabledCoins.add(coin);
    return response;
  }

  /**
   * Enable a coin using native RPC (local node)
   */
  async enableNativeCoin(coin: string): Promise<EnableCoinResponse> {
    const response = await this.rpc('enable', {
      coin,
      mm2: 1
    });

    this.enabledCoins.add(coin);
    return response;
  }

  /**
   * Get balance for a coin
   */
  async getBalance(coin: string): Promise<{ balance: string; unspendable: string }> {
    const response = await this.rpc('my_balance', { coin });
    return {
      balance: response.balance,
      unspendable: response.unspendable_balance
    };
  }

  /**
   * Get orderbook for a trading pair
   */
  async getOrderbook(base: string, rel: string): Promise<Orderbook> {
    const response = await this.rpc('orderbook', { base, rel });
    return response;
  }

  /**
   * Get best orders across all pairs
   */
  async getBestOrders(coin: string, action: 'buy' | 'sell', volume: string): Promise<any> {
    const response = await this.rpc('best_orders', {
      coin,
      action,
      volume
    });
    return response.result;
  }

  /**
   * Place a buy order
   */
  async buy(
    base: string,
    rel: string,
    price: string,
    volume: string
  ): Promise<{ uuid: string }> {
    const response = await this.rpc('buy', {
      base,
      rel,
      price,
      volume
    });
    return { uuid: response.result.uuid };
  }

  /**
   * Place a sell order
   */
  async sell(
    base: string,
    rel: string,
    price: string,
    volume: string
  ): Promise<{ uuid: string }> {
    const response = await this.rpc('sell', {
      base,
      rel,
      price,
      volume
    });
    return { uuid: response.result.uuid };
  }

  /**
   * Place a maker order (set price, wait for taker)
   */
  async setPrice(
    base: string,
    rel: string,
    price: string,
    volume: string,
    cancelPrevious: boolean = true
  ): Promise<{ uuid: string }> {
    const response = await this.rpc('setprice', {
      base,
      rel,
      price,
      volume,
      cancel_previous: cancelPrevious
    });
    return { uuid: response.result.uuid };
  }

  /**
   * Cancel an order
   */
  async cancelOrder(uuid: string): Promise<boolean> {
    const response = await this.rpc('cancel_order', { uuid });
    return response.result === 'success';
  }

  /**
   * Cancel all orders
   */
  async cancelAllOrders(): Promise<{ cancelled: string[]; currently_matching: string[] }> {
    const response = await this.rpc('cancel_all_orders', {
      cancel_by: { type: 'All' }
    });
    return response.result;
  }

  /**
   * Get my orders
   */
  async getMyOrders(): Promise<{
    maker_orders: Record<string, any>;
    taker_orders: Record<string, any>;
  }> {
    const response = await this.rpc('my_orders');
    return response.result;
  }

  /**
   * Get swap status
   */
  async getSwapStatus(uuid: string): Promise<SwapStatus> {
    const response = await this.rpc('my_swap_status', { uuid });
    return response.result;
  }

  /**
   * Get recent swaps
   */
  async getRecentSwaps(limit: number = 10): Promise<SwapStatus[]> {
    const response = await this.rpc('my_recent_swaps', {
      limit,
      from_uuid: null
    });
    return response.result.swaps;
  }

  /**
   * Get all enabled coins
   */
  async getEnabledCoins(): Promise<string[]> {
    const response = await this.rpc('get_enabled_coins');
    return response.result.map((c: any) => c.ticker);
  }

  /**
   * Disable a coin
   */
  async disableCoin(coin: string): Promise<boolean> {
    const response = await this.rpc('disable_coin', { coin });
    this.enabledCoins.delete(coin);
    return response.result.coin === coin;
  }

  /**
   * Get trade fee estimate
   */
  async getTradeFee(coin: string): Promise<{ amount: string; coin: string }> {
    const response = await this.rpc('get_trade_fee', { coin });
    return response.result;
  }

  /**
   * Withdraw coins to an address
   */
  async withdraw(
    coin: string,
    to: string,
    amount: string,
    max: boolean = false
  ): Promise<{ tx_hex: string; tx_hash: string; from: string[]; to: string[]; total_amount: string; spent_by_me: string; received_by_me: string; my_balance_change: string; fee_details: any }> {
    const response = await this.rpc('withdraw', {
      coin,
      to,
      amount: max ? undefined : amount,
      max
    });
    return response;
  }

  /**
   * Send raw transaction
   */
  async sendRawTransaction(coin: string, txHex: string): Promise<string> {
    const response = await this.rpc('send_raw_transaction', {
      coin,
      tx_hex: txHex
    });
    return response.tx_hash;
  }

  /**
   * Get transaction history
   */
  async getTxHistory(coin: string, limit: number = 50): Promise<any[]> {
    const response = await this.rpc('my_tx_history', {
      coin,
      limit,
      from_id: null
    });
    return response.result.transactions;
  }

  /**
   * Check if mm2 is connected and ready
   */
  async isReady(): Promise<boolean> {
    try {
      await this.getVersion();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Initialize common coins for WATTxchange
   */
  async initializeDefaultCoins(): Promise<void> {
    // Enable WTX using local node
    try {
      await this.enableUtxoCoin('WTX', [
        { url: 'wtx-electrum.wattxchange.app:50002', protocol: 'SSL' }
      ]);
    } catch (e) {
      console.warn('Failed to enable WTX:', e);
    }

    // Enable HTH using electrum
    try {
      await this.enableUtxoCoin('HTH', [
        { url: 'electrum.wattxchange.app:50002', protocol: 'SSL' }
      ]);
    } catch (e) {
      console.warn('Failed to enable HTH:', e);
    }

    // Enable BTC with public electrum servers
    try {
      await this.enableUtxoCoin('BTC', [
        { url: 'electrum1.cipig.net:10000', protocol: 'SSL' },
        { url: 'electrum2.cipig.net:10000', protocol: 'SSL' }
      ]);
    } catch (e) {
      console.warn('Failed to enable BTC:', e);
    }

    // Enable LTC with public electrum servers
    try {
      await this.enableUtxoCoin('LTC', [
        { url: 'electrum1.cipig.net:10063', protocol: 'SSL' },
        { url: 'electrum2.cipig.net:10063', protocol: 'SSL' }
      ]);
    } catch (e) {
      console.warn('Failed to enable LTC:', e);
    }
  }
}

export const mm2Service = new MM2Service();
export type { Orderbook, OrderbookEntry, SwapStatus, EnableCoinResponse };
